import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import { useCart } from '../../context/CartContext';

export default function PaymentReturn({ provider, cancelled = false }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const started = useRef(false);
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState(cancelled ? 'Cancelling payment...' : 'Verifying your payment...');
  const orderId = params.get('orderId');

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const complete = async () => {
      if (!orderId) {
        setStatus('error');
        setMessage('The payment return link is incomplete.');
        return;
      }
      try {
        if (cancelled) {
          await api.post('/payments/cancel', { orderId, provider: params.get('provider') });
          setStatus('cancelled');
          setMessage('Payment cancelled. No charge was completed.');
          return;
        }
        if (provider === 'stripe') {
          await api.post('/payments/stripe/verify', { orderId, sessionId: params.get('session_id') });
        } else if (provider === 'paypal') {
          await api.post('/payments/paypal/capture', { orderId, paypalOrderId: params.get('token') });
        }
        clearCart();
        setStatus('success');
        setMessage('Payment confirmed. Your order is ready for processing.');
        window.setTimeout(() => navigate(`/orders/${orderId}`), 1200);
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.message || 'Payment could not be verified. Please contact support before retrying.');
      }
    };
    complete();
  }, [cancelled, clearCart, navigate, orderId, params, provider]);

  return (
    <div className="min-h-[65vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md bg-white border border-[#E8DFD8] rounded-lg p-8 text-center space-y-5 shadow-sm">
        <div className={`w-12 h-12 rounded-full mx-auto flex items-center justify-center text-xl font-bold ${
          status === 'success' ? 'bg-emerald-100 text-emerald-700' : status === 'error' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-[#C49A45]'
        }`}>
          {status === 'success' ? 'OK' : status === 'error' ? '!' : '...'}
        </div>
        <h1 className="font-heading text-2xl font-bold text-[#2B1810]">
          {status === 'success' ? 'Payment Successful' : status === 'cancelled' ? 'Payment Cancelled' : status === 'error' ? 'Payment Needs Attention' : 'Processing Payment'}
        </h1>
        <p className="text-sm text-[#8A796C] leading-relaxed">{message}</p>
        {(status === 'cancelled' || status === 'error') && (
          <div className="flex gap-3 justify-center">
            <Link to="/cart" className="btn-primary">Return to Cart</Link>
            <Link to="/orders" className="btn-outline">My Orders</Link>
          </div>
        )}
      </div>
    </div>
  );
}
